import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../../components/shared/Input';
import { Button } from '../../components/shared/Button';
import { useConfirmarPassword, useOlvidePassword } from '../../hooks/useAuth';
import { ApiError } from '../../api/apiClient';

interface DatosSolicitud {
  usuario: string;
}

interface DatosConfirmacion {
  codigo: string;
  nuevaPassword: string;
  confirmarPassword: string;
}

/** BR-ID-04: reseteo de contraseña self-service — flujo de 2 pasos (Cognito
 * ForgotPassword + ConfirmForgotPassword) en una sola pantalla. */
export function OlvidePasswordPage() {
  const olvidePassword = useOlvidePassword();
  const confirmarPassword = useConfirmarPassword();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState<string>();
  const [error, setError] = useState<string>();
  const [mensaje, setMensaje] = useState<string>();

  const formSolicitud = useForm<DatosSolicitud>();
  const formConfirmacion = useForm<DatosConfirmacion>();

  const solicitarCodigo = (datos: DatosSolicitud) => {
    setError(undefined);
    olvidePassword.mutate(datos, {
      onSuccess: (respuesta) => {
        setUsuario(datos.usuario);
        setMensaje(respuesta.mensaje);
      },
      onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo procesar la solicitud'),
    });
  };

  const confirmarNuevaPassword = (datos: DatosConfirmacion) => {
    if (!usuario) return;
    setError(undefined);
    confirmarPassword.mutate(
      { usuario, codigo: datos.codigo, nuevaPassword: datos.nuevaPassword },
      {
        onSuccess: () => navigate('/admin/login'),
        onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo restablecer la contraseña'),
      },
    );
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-admin-bg to-[oklch(92%_0.01_80)] px-4">
      <div className="w-full max-w-[400px] flex flex-col gap-6 bg-white border border-admin-border rounded-2xl p-8 sm:p-11 shadow-xl">
        <div className="flex flex-col items-center gap-1.5">
          <div className="font-display text-[30px] font-bold text-wood">Santtesanía</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-admin-ink-muted">
            Recuperar contraseña
          </div>
        </div>

        {!usuario ? (
          <form
            onSubmit={formSolicitud.handleSubmit(solicitarCodigo)}
            data-testid="olvide-password-form"
            className="flex flex-col gap-4"
          >
            <p className="text-xs text-admin-ink-muted leading-relaxed">
              Ingresa tu usuario y te enviaremos un código de verificación por correo.
            </p>
            <Input
              label="Usuario"
              placeholder="admin@sacrafts.com"
              error={formSolicitud.formState.errors.usuario?.message}
              {...formSolicitud.register('usuario', { required: 'El usuario es requerido' })}
            />
            {error && <p className="text-xs text-danger" role="alert">{error}</p>}
            <Button type="submit" disabled={olvidePassword.isPending}>
              {olvidePassword.isPending ? 'Enviando…' : 'Enviar código'}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={formConfirmacion.handleSubmit(confirmarNuevaPassword)}
            data-testid="confirmar-password-form"
            className="flex flex-col gap-4"
          >
            {mensaje && (
              <p className="text-xs text-ink-muted leading-relaxed">
                {mensaje} Revisa la bandeja de entrada (y spam) de <strong>{usuario}</strong> —
                el código es un número, no tu correo.
              </p>
            )}
            <Input
              label="Código de verificación (el número que llegó por correo)"
              placeholder="Ej. 123456"
              inputMode="numeric"
              error={formConfirmacion.formState.errors.codigo?.message}
              {...formConfirmacion.register('codigo', { required: 'El código es requerido' })}
            />
            <Input
              label="Nueva contraseña"
              type="password"
              placeholder="••••••••"
              error={formConfirmacion.formState.errors.nuevaPassword?.message}
              {...formConfirmacion.register('nuevaPassword', {
                required: 'La nueva contraseña es requerida',
                minLength: { value: 8, message: 'Debe tener al menos 8 caracteres' },
              })}
            />
            <Input
              label="Confirmar contraseña"
              type="password"
              placeholder="••••••••"
              error={formConfirmacion.formState.errors.confirmarPassword?.message}
              {...formConfirmacion.register('confirmarPassword', {
                required: 'Confirma la nueva contraseña',
                validate: (valor) =>
                  valor === formConfirmacion.watch('nuevaPassword') || 'Las contraseñas no coinciden',
              })}
            />
            {error && <p className="text-xs text-danger" role="alert">{error}</p>}
            <Button type="submit" disabled={confirmarPassword.isPending}>
              {confirmarPassword.isPending ? 'Guardando…' : 'Restablecer contraseña'}
            </Button>
          </form>
        )}

        <Link to="/admin/login" className="text-center text-xs font-semibold text-terracotta">
          Volver a iniciar sesión
        </Link>
      </div>
    </div>
  );
}
